import { Injectable } from "@angular/core";
import { HttpRequest, HttpResponse } from "@angular/common/http";

export interface CacheEntry {
  response: HttpResponse<any>;
  /** Timestamp Unix in ms del momento in cui la risposta è stata salvata */
  timestamp: number;
}

/**
 * Middleware di caching in-memory per le richieste HTTP.
 *
 * Evita race conditions e chiamate duplicate verso lo stesso endpoint
 * con gli stessi parametri nel giro di 2 minuti (TTL configurabile).
 * Il TTL viene valutato al momento del GET: se la entry è scaduta
 * viene eliminata e viene restituito null, forzando una nuova chiamata.
 */
@Injectable({
  providedIn: "root",
})
export class RequestCacheService {
  /** Durata massima in millisecondi di una entry in cache (default: 2 minuti) */
  readonly TTL_MS = 2 * 60 * 1000;

  /** Metodi HTTP considerati cacheabili */
  private readonly cacheableMethods = new Set(["GET", "POST"]);

  /** Map interna: chiave → entry cache */
  private readonly cache = new Map<string, CacheEntry>();

  /**
   * Costruisce la chiave univoca per la richiesta.
   * Basata su metodo HTTP, URL con query params e body serializzato.
   */
  buildCacheKey(request: HttpRequest<any>): string {
    let bodyKey = "";
    if (request.body !== null && request.body !== undefined) {
      try {
        bodyKey = JSON.stringify(request.body);
      } catch {
        bodyKey = String(request.body);
      }
    }
    return `${request.method}:${request.urlWithParams}:${bodyKey}`;
  }

  /**
   * Restituisce la risposta in cache se ancora valida (entro TTL), altrimenti null.
   * Le entry scadute vengono rimosse automaticamente.
   */
  get(request: HttpRequest<any>): HttpResponse<any> | null {
    const key = this.buildCacheKey(request);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > this.TTL_MS) {
      this.cache.delete(key);
      return null;
    }

    return entry.response;
  }

  /**
   * Salva in cache la risposta per la richiesta data.
   */
  put(request: HttpRequest<any>, response: HttpResponse<any>): void {
    const key = this.buildCacheKey(request);
    this.cache.set(key, { response, timestamp: Date.now() });
  }

  /**
   * Determina se la richiesta è cacheabile in base al metodo HTTP.
   * GET e POST sono cacheabili per default.
   */
  isCacheable(request: HttpRequest<any>): boolean {
    return this.cacheableMethods.has(request.method);
  }

  /**
   * Svuota l'intera cache.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Invalida tutte le entry che contengono la stringa URL fornita.
   * Utile dopo operazioni di scrittura (PUT/PATCH/DELETE) per forzare
   * il refresh dei dati correlati.
   */
  invalidateByUrl(url: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(url)) {
        this.cache.delete(key);
      }
    }
  }

  /** Restituisce il numero di entry attualmente in cache. */
  get size(): number {
    return this.cache.size;
  }
}
